import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const DigitalMarketingPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [activeService, setActiveService] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [stats, setStats] = useState({
    roi: 0,
    leads: 0,
    awareness: 0,
    traffic: 0
  });
  
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-section-id');
            setIsVisible(prev => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '-50px' }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  // Animate stats counter
  useEffect(() => {
    if (!isVisible.hero) return;

    const finalStats = {
      roi: 42,
      leads: 65,
      awareness: 38,
      traffic: 150
    };

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const counters = {};
    Object.keys(finalStats).forEach(key => {
      counters[key] = setInterval(() => {
        setStats(prev => {
          const current = prev[key];
          const target = finalStats[key];
          const increment = (target - current) / 10;
          return {
            ...prev,
            [key]: current + increment > target ? target : current + increment
          };
        });
      }, interval);
    });

    setTimeout(() => {
      Object.values(counters).forEach(clearInterval);
      setStats(finalStats);
    }, duration);

    return () => {
      Object.values(counters).forEach(clearInterval);
    };
  }, [isVisible.hero]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Thank you ${formData.name}! We'll contact you about ${formData.service || 'digital marketing'} soon.`);
    setFormData({
      name: '',
      email: '',
      company: '',
      service: '',
      message: ''
    });
  };

  const services = [
    {
      id: 1,
      icon: "🎯",
      title: t('digitalMarketingPage.services.items.0.title'),
      description: t('digitalMarketingPage.services.items.0.description'),
      features: [
        t('digitalMarketingPage.services.items.0.features.0'),
        t('digitalMarketingPage.services.items.0.features.1'),
        t('digitalMarketingPage.services.items.0.features.2'),
        t('digitalMarketingPage.services.items.0.features.3'),
        t('digitalMarketingPage.services.items.0.features.4')
      ]
    },
    {
      id: 2,
      icon: "📱",
      title: t('digitalMarketingPage.services.items.1.title'),
      description: t('digitalMarketingPage.services.items.1.description'),
      features: [
        t('digitalMarketingPage.services.items.1.features.0'),
        t('digitalMarketingPage.services.items.1.features.1'),
        t('digitalMarketingPage.services.items.1.features.2'),
        t('digitalMarketingPage.services.items.1.features.3'),
        t('digitalMarketingPage.services.items.1.features.4')
      ]
    },
    {
      id: 3,
      icon: "📧",
      title: t('digitalMarketingPage.services.items.2.title'),
      description: t('digitalMarketingPage.services.items.2.description'),
      features: [
        t('digitalMarketingPage.services.items.2.features.0'),
        t('digitalMarketingPage.services.items.2.features.1'),
        t('digitalMarketingPage.services.items.2.features.2'),
        t('digitalMarketingPage.services.items.2.features.3'),
        t('digitalMarketingPage.services.items.2.features.4')
      ]
    },
    {
      id: 4,
      icon: "🔍",
      title: t('digitalMarketingPage.services.items.3.title'),
      description: t('digitalMarketingPage.services.items.3.description'),
      features: [
        t('digitalMarketingPage.services.items.3.features.0'),
        t('digitalMarketingPage.services.items.3.features.1'),
        t('digitalMarketingPage.services.items.3.features.2'),
        t('digitalMarketingPage.services.items.3.features.3'),
        t('digitalMarketingPage.services.items.3.features.4')
      ]
    },
    {
      id: 5,
      icon: "📝",
      title: t('digitalMarketingPage.services.items.4.title'),
      description: t('digitalMarketingPage.services.items.4.description'),
      features: [
        t('digitalMarketingPage.services.items.4.features.0'),
        t('digitalMarketingPage.services.items.4.features.1'),
        t('digitalMarketingPage.services.items.4.features.2'),
        t('digitalMarketingPage.services.items.4.features.3'),
        t('digitalMarketingPage.services.items.4.features.4')
      ]
    },
    {
      id: 6,
      icon: "📊",
      title: t('digitalMarketingPage.services.items.5.title'),
      description: t('digitalMarketingPage.services.items.5.description'),
      features: [
        t('digitalMarketingPage.services.items.5.features.0'),
        t('digitalMarketingPage.services.items.5.features.1'),
        t('digitalMarketingPage.services.items.5.features.2'),
        t('digitalMarketingPage.services.items.5.features.3'),
        t('digitalMarketingPage.services.items.5.features.4')
      ]
    },
    {
      id: 7,
      icon: "🎨",
      title: t('digitalMarketingPage.services.items.6.title'),
      description: t('digitalMarketingPage.services.items.6.description'),
      features: [
        t('digitalMarketingPage.services.items.6.features.0'),
        t('digitalMarketingPage.services.items.6.features.1'),
        t('digitalMarketingPage.services.items.6.features.2'),
        t('digitalMarketingPage.services.items.6.features.3'),
        t('digitalMarketingPage.services.items.6.features.4')
      ]
    },
    {
      id: 8,
      icon: "📈",
      title: t('digitalMarketingPage.services.items.7.title'),
      description: t('digitalMarketingPage.services.items.7.description'),
      features: [
        t('digitalMarketingPage.services.items.7.features.0'),
        t('digitalMarketingPage.services.items.7.features.1'),
        t('digitalMarketingPage.services.items.7.features.2'),
        t('digitalMarketingPage.services.items.7.features.3'),
        t('digitalMarketingPage.services.items.7.features.4')
      ]
    }
  ];

  const testimonials = [
    {
      name: t('digitalMarketingPage.testimonials.items.0.name'),
      role: t('digitalMarketingPage.testimonials.items.0.role'),
      comment: t('digitalMarketingPage.testimonials.items.0.comment'),
      avatar: "SJ"
    },
    {
      name: t('digitalMarketingPage.testimonials.items.1.name'),
      role: t('digitalMarketingPage.testimonials.items.1.role'),
      comment: t('digitalMarketingPage.testimonials.items.1.comment'),
      avatar: "MC"
    },
    {
      name: t('digitalMarketingPage.testimonials.items.2.name'),
      role: t('digitalMarketingPage.testimonials.items.2.role'),
      comment: t('digitalMarketingPage.testimonials.items.2.comment'),
      avatar: "ER"
    }
  ];

  const reasons = [
    {
      number: "1",
      title: t('digitalMarketingPage.reasons.items.0.title'),
      description: t('digitalMarketingPage.reasons.items.0.description')
    },
    {
      number: "2",
      title: t('digitalMarketingPage.reasons.items.1.title'),
      description: t('digitalMarketingPage.reasons.items.1.description')
    },
    {
      number: "3",
      title: t('digitalMarketingPage.reasons.items.2.title'),
      description: t('digitalMarketingPage.reasons.items.2.description')
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Custom Animation Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
        
        @keyframes progress {
          0% { width: 0%; }
          100% { width: var(--target-width); }
        }
        
        @keyframes gradient {
          0% { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .animate-on-scroll.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .animate-stagger > * {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .animate-stagger.visible > *:nth-child(1) { transition-delay: 0.1s; }
        .animate-stagger.visible > *:nth-child(2) { transition-delay: 0.2s; }
        .animate-stagger.visible > *:nth-child(3) { transition-delay: 0.3s; }
        .animate-stagger.visible > *:nth-child(4) { transition-delay: 0.4s; }
        .animate-stagger.visible > *:nth-child(5) { transition-delay: 0.5s; }
        .animate-stagger.visible > *:nth-child(6) { transition-delay: 0.6s; }
        .animate-stagger.visible > *:nth-child(7) { transition-delay: 0.7s; }
        .animate-stagger.visible > *:nth-child(8) { transition-delay: 0.8s; }
        
        .animate-stagger.visible > * {
          opacity: 1;
          transform: translateY(0);
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .animate-gradient {
          background: linear-gradient(90deg, #dc2626, #4b5563, #dc2626);
          background-size: 200% auto;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradient 3s linear infinite;
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite linear;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          background-size: 1000px 100%;
        }
        
        .hover-lift {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .hover-lift:hover {
          transform: translateY(-8px);
        }
      `}</style>

      {/* ===== HERO SECTION ===== */}
      <section 
        className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white py-20 md:py-32"
        ref={el => sectionsRef.current[0] = el}
        data-section-id="hero"
      >
        <img
          src="/imggg1.jpg" 
          alt="Digital Marketing Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        
        <div className="absolute inset-0 bg-black/10 z-1"></div>
        
        {/* Animated floating elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/10 rounded-full animate-float blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full animate-float blur-3xl" style={{animationDelay: '2s'}}></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-on-scroll ${isVisible.hero ? 'visible' : ''}`}>
            {/* LEFT CONTENT */}
            <div className="text-left">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                <span className="text-gray-900">
                  {t('digitalMarketingPage.hero.title1')}
                </span>
                <br />
                <span className="mt-2 inline-block animate-gradient">
                  {t('digitalMarketingPage.hero.title2')}
                </span>
              </h1>
              <p className="mt-6 text-xl md:text-2xl text-gray-900 max-w-3xl leading-relaxed">
                {t('digitalMarketingPage.hero.description')}
              </p>
            </div>

            {/* RIGHT IMAGE */}
            <div className="flex justify-center lg:justify-end relative">
              <div className="relative group">
                <img
                  src="/digital.png"
                  alt="Digital Marketing"
                  className="w-full max-w-lg rounded-xl shadow-xl transform transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-red-600/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>
          </div>

          {/* STATS GRID */}
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 animate-stagger ${isVisible.hero ? 'visible' : ''}`}>
            {[
              { 
                icon: "💰", 
                value: stats.roi.toFixed(0), 
                label: t('digitalMarketingPage.stats.roi'), 
                suffix: "%" 
              },
              { 
                icon: "👥", 
                value: stats.leads.toFixed(0), 
                label: t('digitalMarketingPage.stats.leads'), 
                suffix: "%" 
              },
              { 
                icon: "📢", 
                value: stats.awareness.toFixed(0), 
                label: t('digitalMarketingPage.stats.awareness'), 
                suffix: "%" 
              },
              { 
                icon: "🚀", 
                value: stats.traffic.toFixed(0), 
                label: t('digitalMarketingPage.stats.traffic'), 
                suffix: "%" 
              }
            ].map((stat, index) => (
              <div 
                key={index}
                className="bg-gray-50/90 border border-gray-200 p-6 rounded-xl hover:border-red-300 hover:shadow-lg transition-all duration-500 backdrop-blur-sm hover-lift relative overflow-hidden group"
              >
                <div className="absolute inset-0 animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="text-red-600 mb-3 text-2xl animate-pulse">{stat.icon}</div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section 
        id="services" 
        className="relative py-12 md:py-16 bg-gray-50"
        ref={el => sectionsRef.current[1] = el}
        data-section-id="services"
      >
        <img
          src="/greyy.avif" 
          alt="Digital Marketing Services Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        
        <div className="relative z-10">
          <div className={`text-center mb-8 md:mb-12 animate-on-scroll ${isVisible.services ? 'visible' : ''}`}>
            <h2 className="text-[24px] md:text-[26px] lg:text-[28px] font-bold mb-3 text-gray-900 leading-tight">
              {t('digitalMarketingPage.services.title')}
            </h2>
            <p className="text-gray-900 max-w-xl mx-auto text-[16px] md:text-[17px] leading-relaxed">
              {t('digitalMarketingPage.services.subtitle')}
            </p>
          </div>

          <div className="max-w-7xl mx-auto px-4">
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 animate-stagger ${isVisible.services ? 'visible' : ''}`}>
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className={`group relative transition-all duration-500 ease-out cursor-pointer flex flex-col ${
                    activeService === index
                      ? 'rounded-xl border-red-600 shadow-lg shadow-red-500/10 bg-white/95 backdrop-blur-sm scale-105'
                      : 'rounded-lg border-gray-200 bg-white/95 backdrop-blur-sm hover:border-gray-300'
                  }`}
                  style={{
                    borderWidth: '1px',
                    height: activeService === index ? 'auto' : '140px',
                    margin: activeService === index ? '0' : '0 auto',
                    padding: activeService === index ? '1.5rem' : '1rem',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',
                    maxWidth: activeService === index ? 'none' : '100%',
                  }}
                  onMouseEnter={() => setActiveService(index)}
                  onMouseLeave={() => setActiveService(null)}
                >
                  {/* Hover Gradient */}
                  {activeService === index && (
                    <div
                      className="absolute inset-0 bg-gradient-to-br from-red-50/30 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    ></div>
                  )}

                  <div className="relative flex flex-col h-full">
                    {/* Icon and Title Container */}
                    <div className={`flex flex-col items-center justify-center flex-1 ${
                      activeService === index ? '' : 'h-full'
                    }`}>
                      {/* Icon */}
                      <div className={`text-red-600 transition-all duration-300 animate-bounce ${
                        activeService === index ? 'scale-110 mb-3' : 'scale-100 mb-4'
                      }`}>
                        {service.icon}
                      </div>

                      {/* Title */}
                      <h3 className={`text-center font-bold text-gray-900 transition-all duration-300 ${
                        activeService === index 
                          ? 'text-[17px]' 
                          : 'text-[14px]'
                      }`}>
                        {service.title}
                      </h3>
                    </div>

                    {/* Details */}
                    {activeService === index && (
                      <div className="animate-fadeIn mt-4">
                        <p className="text-gray-600 mb-4 text-[14px] leading-relaxed text-center">
                          {service.description}
                        </p>

                        <ul className="space-y-2 mb-4">
                          {service.features.map((feature, i) => (
                            <li 
                              key={i} 
                              className="flex items-start text-[13px] leading-snug opacity-0"
                              style={{animation: `slideInLeft 0.5s ease-out forwards`, animationDelay: `${i * 0.1}s`}}
                            >
                              <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2 mt-1.5 flex-shrink-0 animate-pulse"></div>
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US SECTION ===== */}
      <section 
        className="relative py-20 bg-white overflow-hidden"
        ref={el => sectionsRef.current[2] = el}
        data-section-id="why-choose"
      >
        <img
          src="/imggg2.jpg"
          alt="Why Choose Our Marketing Agency Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
          <div className={`text-center mb-16 animate-on-scroll ${isVisible['why-choose'] ? 'visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('digitalMarketingPage.reasons.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('digitalMarketingPage.reasons.subtitle')}
            </p>
          </div>
          
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-stagger ${isVisible['why-choose'] ? 'visible' : ''}`}>
            <div>
              <div className="mb-8">
                {reasons.map((reason, index) => (
                  <div key={index} className="flex items-start mb-8 group">
                    <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 animate-pulse" style={{animationDelay: `${index * 0.5}s`}}>
                      <span className="text-white font-bold text-xl">{reason.number}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors duration-300">{reason.title}</h3>
                      <p className="text-gray-600">{reason.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white/95 backdrop-blur-sm border border-gray-200/80 rounded-2xl p-8 shadow-lg hover-lift relative overflow-hidden">
              <div className="absolute inset-0 animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('digitalMarketingPage.performance.title')}</h3>
              <div className="space-y-6">
                {[
                  {
                    label: t('digitalMarketingPage.performance.metrics.0.label'),
                    value: `+${stats.roi.toFixed(0)}%`,
                    width: '75%',
                    color: 'from-green-500 to-emerald-500',
                    delay: '0.1s'
                  },
                  {
                    label: t('digitalMarketingPage.performance.metrics.1.label'),
                    value: `+${stats.leads.toFixed(0)}%`,
                    width: '80%',
                    color: 'from-blue-500 to-cyan-500',
                    delay: '0.3s'
                  },
                  {
                    label: t('digitalMarketingPage.performance.metrics.2.label'),
                    value: `+${stats.awareness.toFixed(0)}%`,
                    width: '66%',
                    color: 'from-purple-500 to-pink-500',
                    delay: '0.5s'
                  }
                ].map((metric, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-700">{metric.label}</span>
                      <span className="font-bold text-green-600 animate-pulse" style={{animationDelay: metric.delay}}>{metric.value}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-2 bg-gradient-to-r ${metric.color} rounded-full animate-progress`} 
                        style={{width: metric.width, animationDelay: metric.delay}}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS SECTION ===== */}
      <section 
        className="relative py-20 bg-gray-50"
        ref={el => sectionsRef.current[3] = el}
        data-section-id="testimonials"
      >
        <img
          src="/pic1.jpg"
          alt="Testimonials Background"
          className="absolute inset-0 w-full h-full object-cover z-0 opacity-20"
        />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className={`text-center mb-16 animate-on-scroll ${isVisible.testimonials ? 'visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('digitalMarketingPage.testimonials.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('digitalMarketingPage.testimonials.subtitle')}
            </p>
          </div>
          
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 animate-stagger ${isVisible.testimonials ? 'visible' : ''}`}>
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl hover:border-red-300 transition-all duration-500 hover-lift group relative overflow-hidden"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-blue-600 rounded-2xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                <div className="relative">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                      <p className="text-gray-600 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.comment}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section 
        className="py-12 md:py-20 bg-gradient-to-br from-gray-900 to-black text-white"
        ref={el => sectionsRef.current[4] = el}
        data-section-id="cta"
      >
        <div className="absolute inset-0 animate-gradient bg-gradient-to-r from-red-600/20 via-transparent to-blue-600/20 bg-[size:200%_100%]"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center relative z-10">
          <h2 className={`text-[30px] font-bold mb-4 md:mb-6 animate-on-scroll ${isVisible.cta ? 'visible' : ''}`}>
            {t('digitalMarketingPage.cta.title1')}{" "}
            <span className="text-red-400 animate-pulse">{t('digitalMarketingPage.cta.title2')}</span>
          </h2>
          <p className={`text-[22px] text-gray-300 mb-8 md:mb-10 max-w-2xl mx-auto px-4 animate-on-scroll ${isVisible.cta ? 'visible' : ''}`}>
            {t('digitalMarketingPage.cta.description')}
          </p>
          
          <div className={`flex flex-col sm:flex-row gap-3 justify-center mb-6 md:mb-8 animate-on-scroll ${isVisible.cta ? 'visible' : ''}`}>
            <a
              href="/contact"
              className="group inline-flex items-center justify-center gap-2
                         bg-red-600 text-white
                         px-4 py-2 md:px-6 md:py-3
                         rounded-full
                         font-semibold text-sm md:text-base
                         shadow-sm shadow-red-600/20
                         hover:bg-red-700
                         transition-all duration-300
                         transform hover:scale-105
                         animate-pulse"
            >
              {t('digitalMarketingPage.cta.button')}
              <svg
                className="w-4 h-4 md:w-5 md:h-5 opacity-0 -translate-x-1
                           group-hover:opacity-100 group-hover:translate-x-0
                           transition-all duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </a>
          </div>

          <p className={`mt-8 md:mt-10 text-gray-400 text-base px-4 md:px-0 animate-on-scroll ${isVisible.cta ? 'visible' : ''}`}>
            {t('digitalMarketingPage.cta.subtext')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default DigitalMarketingPage;